const aiLessonContent = {
  "What is AI, really?": {
    introduction: "Artificial intelligence (AI) is technology that performs tasks commonly associated with human intelligence, such as recognizing patterns, understanding language, generating content, and making predictions.",
    sections: [
      {
        title: "1. Understand the concept",
        content: "AI systems learn patterns from data or follow programmed rules. Generative AI tools can create new text, images, audio, or code based on the instructions they receive."
      },
      {
        title: "2. Everyday examples",
        content: "Email spam filters classify messages. Navigation apps estimate traffic and routes. Recommendation systems suggest videos or products. AI chatbots respond to questions and help draft content."
      },
      {
        title: "3. Guided practice",
        content: "Choose one task you perform regularly, such as drafting an email or organizing information. Describe how an AI tool might help, what information it would need, and what a person should verify."
      },
      {
        title: "4. Independent exercise",
        content: "List three examples of AI you have encountered. For each, identify the task it performs and one possible limitation."
      }
    ],
    reflection: "What is one task where AI could save you time, and what would you still need to check yourself?"
  },

  "Responsible AI & you": {
    introduction: "Responsible AI means using AI in ways that are safe, fair, transparent, privacy-conscious, and accountable.",
    sections: [
      {
        title: "1. Check accuracy",
        content: "AI can produce incorrect or fabricated information. Verify important claims using reliable, independent sources, especially for health, legal, financial, or safety-related decisions."
      },
      {
        title: "2. Protect privacy",
        content: "Do not enter passwords, private customer records, financial credentials, or confidential business information into an AI service unless its approved privacy and security arrangements explicitly permit it."
      },
      {
        title: "3. Consider fairness",
        content: "AI outputs can reflect biases in data or design. Review results for stereotypes, unfair assumptions, exclusion, and inappropriate recommendations."
      },
      {
        title: "4. Independent exercise",
        content: "Review a sample AI-generated answer. Identify two claims to verify, any personal information that should not be shared, and one decision that requires human judgment."
      }
    ],
    reflection: "What checks would you perform before using AI-generated information in a real assignment or business task?"
  },

  "Your first prompt": {
    introduction: "A prompt is the instruction or question you give an AI tool. Clear prompts provide context, define the task, and explain what a useful result should look like.",
    sections: [
      {
        title: "1. Build a clear prompt",
        content: "Include a role or perspective when useful, the task, relevant context, the intended audience, and the desired output format. Avoid sharing confidential information."
      },
      {
        title: "2. Example",
        content: "Weak prompt: 'Write about computers.' Improved prompt: 'Explain three everyday uses of computers to a beginner in simple English. Give one practical example for each use and finish with a short recap.'"
      },
      {
        title: "3. Guided practice",
        content: "Choose a topic you know. Write a prompt that specifies the task, audience, context, and format. Run it in an AI tool and check whether the result follows your instructions."
      },
      {
        title: "4. Independent exercise",
        content: "Rewrite a vague request into a precise prompt. Compare the two outputs and note which instructions made the biggest difference."
      }
    ],
    reflection: "Which missing detail most often makes an AI response less useful: context, audience, task, or output format?"
  },

  "Prompt Engineering Foundations": {
    introduction: "Prompt engineering is the practice of designing, testing, and refining instructions so an AI system produces more useful results.",
    sections: [
      {
        title: "1. Specify the outcome",
        content: "State what the AI should produce and what success looks like. For example, request five headline ideas for a defined audience rather than simply asking for marketing help."
      },
      {
        title: "2. Add constraints",
        content: "Set relevant boundaries such as length, tone, reading level, format, and information that must or must not be included."
      },
      {
        title: "3. Iterate systematically",
        content: "Review the output against your requirements. Change one important instruction at a time so you can understand which change improved the result."
      },
      {
        title: "4. Independent exercise",
        content: "Create a prompt for a 150-word beginner-friendly explanation of a digital skill. Include the audience, tone, required example, and output length. Evaluate the result against those requirements."
      }
    ],
    reflection: "How could you tell whether a prompt is successful without relying only on whether the output sounds convincing?"
  },

  "Advanced prompting patterns": {
    introduction: "Advanced prompting uses structured instructions, examples, decomposition, and explicit evaluation criteria to handle more complex tasks.",
    sections: [
      {
        title: "1. Break down complex tasks",
        content: "Divide a large request into smaller stages, such as research questions, outline, draft, review, and final formatting. Verify each stage before relying on the next."
      },
      {
        title: "2. Provide examples",
        content: "Show the AI a short example of the format or style you want. Examples should illustrate the desired pattern without exposing private or copyrighted material unnecessarily."
      },
      {
        title: "3. Define evaluation criteria",
        content: "Specify measurable requirements such as factual support, completeness, readability, audience fit, and formatting. Ask for uncertainty to be identified rather than hidden."
      },
      {
        title: "4. Independent exercise",
        content: "Design a multi-step prompt for planning a beginner workshop. Include the audience, objectives, agenda, required materials, and a final checklist. Review the output for missing or unsupported details."
      }
    ],
    reflection: "Which part of a complex task should be checked by a person before the next stage begins?"
  },

  "Prompt debugging": {
    introduction: "Prompt debugging means diagnosing why an AI response missed the goal and making targeted changes to improve it.",
    sections: [
      {
        title: "1. Identify the failure",
        content: "Check whether the output misunderstood the task, omitted requirements, used the wrong tone, invented facts, or followed the wrong format."
      },
      {
        title: "2. Find the cause",
        content: "Look for missing context, conflicting instructions, vague terms, excessive scope, or unclear success criteria."
      },
      {
        title: "3. Make a targeted revision",
        content: "Clarify the task, add necessary context, or provide an example. Change one major factor at a time and compare the new result with the original."
      },
      {
        title: "4. Independent exercise",
        content: "Take an AI answer that is too long or too vague. Identify the problem, rewrite the prompt with specific requirements, and evaluate whether the revised answer improves."
      }
    ],
    reflection: "What evidence would convince you that your revised prompt actually improved the result?"
  },

  "Custom GPTs": {
    introduction: "A custom GPT or configured AI assistant is tailored for a particular workflow using instructions and, where supported, approved reference material and tools.",
    sections: [
      {
        title: "1. Define one purpose",
        content: "Start with a focused use case, such as helping learners practise vocabulary or helping a team draft consistent project summaries."
      },
      {
        title: "2. Write operating instructions",
        content: "Describe the assistant's role, intended users, workflow, response format, boundaries, and when it should ask clarifying questions or admit uncertainty."
      },
      {
        title: "3. Prepare reference material",
        content: "Use current, relevant documents that you have permission to use. Remove unnecessary personal or confidential data and check whether the assistant accurately represents its sources."
      },
      {
        title: "4. Independent exercise",
        content: "Draft a one-page specification for a study assistant. Include its purpose, three tasks it should perform, three things it must avoid, and five test questions."
      }
    ],
    reflection: "How would you test whether your configured assistant follows its instructions consistently?"
  },

  "AI agents & automation": {
    introduction: "An AI agent is a system that can use a model, instructions, and sometimes tools to carry out steps toward a goal. Automation connects steps so a workflow can run with less manual effort.",
    sections: [
      {
        title: "1. Map the workflow",
        content: "Describe the trigger, inputs, processing steps, decisions, outputs, and failure cases before selecting tools."
      },
      {
        title: "2. Set permissions and safeguards",
        content: "Give the system only the access it needs. Require human approval for consequential actions such as sending external messages, spending money, deleting data, or changing production systems."
      },
      {
        title: "3. Test safely",
        content: "Start with sample data or a sandbox. Check incorrect inputs, tool failures, duplicate actions, logging, and recovery procedures before real use."
      },
      {
        title: "4. Independent exercise",
        content: "Map a simple workflow, such as turning a submitted assignment into a draft feedback report. Mark which steps can be automated and where a teacher must review the result."
      }
    ],
    reflection: "What is the most important safeguard in your proposed workflow, and what could happen if it failed?"
  },

  "Capstone kickoff": {
    introduction: "A capstone project combines the concepts from a course into a practical solution to a clearly defined problem.",
    sections: [
      {
        title: "1. Choose a real problem",
        content: "Select a manageable problem for a real audience. Explain who experiences it, why it matters, and what a successful solution should achieve."
      },
      {
        title: "2. Define the solution",
        content: "Describe the proposed AI-assisted workflow, its inputs and outputs, the tools required, and the role of human review."
      },
      {
        title: "3. Plan validation",
        content: "Choose simple success measures. Test the solution with representative examples, check factual accuracy and privacy risks, and document limitations."
      },
      {
        title: "4. Independent exercise",
        content: "Prepare a one-page project brief containing the problem, intended users, proposed workflow, expected outcome, evaluation criteria, risks, and next steps."
      }
    ],
    reflection: "What evidence will demonstrate that your project solves the problem rather than simply producing an impressive AI output?"
  }
};

export default aiLessonContent;
